import { MouseEvent, useEffect } from "react";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm, usePage } from "@inertiajs/react";
import { useAtomValue, useSetAtom } from "jotai";
import { RESET } from "jotai/utils";

import {
    Box,
    Flex,
    Image,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Text
} from "@chakra-ui/react";

import logoSvg from "@assets/img/logo-black-and-white.svg";

import { userAtom } from "@atoms/parentsAtom.ts";

import { truncateName } from "@utils/utils.ts";

import NavItem from "../NavItem/NavItem.tsx";
import MobileNav from "./MobileNav.tsx";

const ParentsNav = () => {
    const { auth } = usePage().props;
    const parentUser = useAtomValue(userAtom);
    const parentUserReset = useSetAtom(userAtom);
    const setParentUser = useSetAtom(userAtom);
    const { post } = useForm({});

    const handleLogout = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        post(route("logout"), {
            replace: true
        });
        parentUserReset(RESET);
    };

    useEffect(() => {
        if (auth) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const authData: any = auth;
            setParentUser(prevUser => ({
                ...prevUser,
                id: authData?.id,
                accountType: authData?.account_type,
                email: authData?.email,
                name: authData?.first_name,
                displayName: authData?.display_name,
                auth0Username: authData?.auth0_username || "",
                firstName: authData?.first_name,
                lastName: authData?.last_name,
                status: authData?.status || "",
                is_enrolled: authData?.is_enrolled,
                auth0Id: authData?.auth0_id || "",
                chargebeeId: authData?.chargebee_id || "",
                hubspotId: authData?.hubspot_id || "",
                wpEcomUserId: authData?.wp_ecom_user_id
                    ? authData?.wp_ecom_user_id.toString()
                    : "",
                wp_lms_user_id: authData?.wp_lms_user_id
                    ? parseInt(authData?.wp_lms_user_id)
                    : 0,
                createdAt: authData?.created_at || "",
                updatedAt: authData?.updated_at || "",
                deletedAt: authData?.deleted_at || "",
                lastUpdatedAt: authData?.last_updated_at || "",
                pendingStudentEnrolment: authData?.pending_student_enrolment,
                license_expiry_at_text: authData?.license_expiry_at_text || "",
                has_expired_license: authData?.has_expired_license
            }));
        }
    }, [auth]);

    return (
        <Flex
            height="4rem"
            borderBottom="1px"
            borderBottomColor="gray.300"
            px={10}
            py={2}
            bg="gray.50"
            justify="space-between"
            alignItems="center"
            wrap="wrap"
            w="100%"
        >
            <a href={route("parents.dashboard")}>
                <Image
                    src={logoSvg}
                    alt="Euka Logo"
                    pointerEvents="none"
                    userSelect="none"
                    width="74px"
                    height="32px"
                />
            </a>

            {parentUser && parentUser.id !== 0 && (
                <>
                    <Flex display={{ base: "flex", md: "none" }}>
                        <MobileNav handleLogout={handleLogout} />
                    </Flex>

                    <Flex
                        gap="2rem"
                        alignItems="center"
                        marginInline="auto"
                        marginTop="3px"
                        display={{ base: "none", md: "flex" }}
                    >
                        <NavItem
                            url={route("parents.dashboard")}
                            linkText="Home"
                        />
                        <NavItem
                            url={route("parents.manage-programs")}
                            linkText="Manage programs"
                        />
                        <NavItem
                            url={route("parents.student-documents")}
                            linkText="Student documents"
                        />
                        <NavItem
                            url={route("external.ecom.resources")}
                            linkText="Resource centre"
                            isExternal
                        />
                        <NavItem
                            url={route("external.ecom.help")}
                            linkText="Help"
                            isExternal
                        />
                    </Flex>
                </>
            )}
            {parentUser && parentUser.id !== 0 && (
                <Box display={{ base: "none", md: "flex" }}>
                    <Menu>
                        {({ isOpen }) => (
                            <>
                                <MenuButton
                                    cursor="pointer"
                                    title={parentUser && parentUser.displayName}
                                    textDecor="none"
                                    _hover={{
                                        textDecor: "none"
                                    }}
                                >
                                    <Text
                                        fontWeight={600}
                                        display="flex"
                                        gap={2}
                                    >
                                        {truncateName(
                                            parentUser && parentUser.displayName
                                        )}
                                        <Box
                                            as="span"
                                            transition="transform 0.2s ease"
                                            transform={
                                                isOpen
                                                    ? "rotate(180deg)"
                                                    : "rotate(0)"
                                            }
                                            transformOrigin="center"
                                            color="gray.600"
                                        >
                                            <FontAwesomeIcon
                                                icon={faChevronDown}
                                                fontSize={14}
                                            />
                                        </Box>
                                    </Text>
                                </MenuButton>
                                <MenuList
                                    borderRadius="1rem"
                                    boxShadow="0px 4px 20px -2px rgba(50, 50, 71, 0.08), 0px 0px 1px 0px rgba(12, 26, 75, 0.10)"
                                    borderColor="gray.200"
                                >
                                    <MenuItem
                                        as="a"
                                        href={route("parents.account-details")}
                                        padding="0.5rem 1rem"
                                        fontWeight={600}
                                    >
                                        Account details
                                    </MenuItem>
                                    <MenuItem
                                        as="a"
                                        href={route("parents.shared-access")}
                                        padding="0.5rem 1rem"
                                        fontWeight={600}
                                    >
                                        Shared access
                                    </MenuItem>
                                    <MenuDivider marginInline={4} />
                                    <MenuItem
                                        as="a"
                                        href={route("external.lms.home")}
                                        target="_blank"
                                        padding="0.5rem 1rem"
                                    >
                                        Switch to student portal
                                    </MenuItem>
                                    <MenuItem
                                        padding="0.5rem 1rem"
                                        onClick={handleLogout}
                                    >
                                        Logout
                                    </MenuItem>
                                </MenuList>
                            </>
                        )}
                    </Menu>
                </Box>
            )}
        </Flex>
    );
};

export default ParentsNav;
